import React, { useState } from 'react';
import { useTenant } from './contexts/TenantContext';
import { useAuth } from './contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

function SetupWizard({ onComplete }) {
  const { tenant, businessName, refreshTenant } = useTenant();
  const { session } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Business info
  const [bizName, setBizName] = useState(tenant?.business_name && tenant.business_name !== 'My Business' ? tenant.business_name : '');
  const [bizPhone, setBizPhone] = useState(tenant?.phone || '');
  const [bizEmail, setBizEmail] = useState(tenant?.email || '');
  const [bizAddress, setBizAddress] = useState(tenant?.address || '');

  // Step 2: First service
  const [serviceName, setServiceName] = useState('');
  const [serviceDuration, setServiceDuration] = useState('60');
  const [servicePrice, setServicePrice] = useState('');
  const [services, setServices] = useState([]);

  // Step 3: Hours
  const [hours, setHours] = useState({
    0: { open: false, start: '09:00', end: '17:00' },
    1: { open: true, start: '09:00', end: '19:00' },
    2: { open: true, start: '09:00', end: '19:00' },
    3: { open: true, start: '09:00', end: '19:00' },
    4: { open: true, start: '09:00', end: '19:00' },
    5: { open: true, start: '09:00', end: '19:00' },
    6: { open: true, start: '10:00', end: '17:00' },
  });

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const accent = '#ff6b2b';
  const accent2 = '#ffab40';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + (session?.access_token || ''),
  });

  // Step 1: Save business info
  const saveBusiness = async () => {
    if (!bizName.trim()) { setError('Business name is required'); return; }
    setLoading(true);
    setError('');
    try {
      var res = await fetch(API_URL + '/api/business-settings', {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ business_name: bizName.trim(), phone: bizPhone, email: bizEmail, address: bizAddress }),
      });
      var data = await res.json();
      if (data.error) throw new Error(data.error);
      await refreshTenant();
      setStep(2);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Step 2: Add services
  const addService = async () => {
    if (!serviceName.trim() || !servicePrice) { setError('Service name and price required'); return; }
    setLoading(true);
    setError('');
    try {
      var res = await fetch(API_URL + '/api/services', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          name: serviceName.trim(),
          duration: parseInt(serviceDuration),
          price: parseFloat(servicePrice),
          is_active: true,
        }),
      });
      var data = await res.json();
      if (data.error) throw new Error(data.error);
      setServices(prev => [...prev, { name: serviceName, duration: serviceDuration, price: servicePrice }]);
      setServiceName('');
      setServicePrice('');
      setServiceDuration('60');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Step 3: Save hours
  const saveHours = async () => {
    setLoading(true);
    setError('');
    try {
      var hoursArray = Object.entries(hours).map(function(entry) {
        var day = parseInt(entry[0]);
        var h = entry[1];
        return {
          day_of_week: day,
          is_closed: !h.open,
          open_time: h.start,
          close_time: h.end,
        };
      });
      var res = await fetch(API_URL + '/api/business-settings/hours', {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ hours: hoursArray }),
      });
      var data = await res.json();
      if (data.error) throw new Error(data.error);
      setStep(4);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const bookingUrl = window.location.origin + '/book/' + (tenant?.subdomain || 'your-business');
  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setError('');
    alert('Link copied!');
  };

  const finishSetup = () => {
    if (onComplete) onComplete();
  };

  var inputStyle = {
    width: '100%', padding: '14px 18px', background: '#252840', border: '1.5px solid #2a2a4a',
    borderRadius: 12, fontSize: 15, color: '#fff', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  var selectStyle = {
    ...inputStyle, appearance: 'auto',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 520, width: '100%' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: '#111' }}>iV</span>
          </div>
          <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 800, margin: '0 0 6px' }}>
            {step === 1 ? 'Welcome to IVision!' : step === 2 ? 'Add Your Services' : step === 3 ? 'Set Your Hours' : 'You\'re All Set!'}
          </h1>
          <p style={{ color: '#888', fontSize: 14, margin: 0 }}>
            {step === 1 ? 'Let\'s get your business set up in 60 seconds' : step === 2 ? 'What services do you offer?' : step === 3 ? 'When are you open?' : 'Your online booking page is live!'}
          </p>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
          {[1, 2, 3, 4].map(function(s) {
            return (
              <div key={s} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: s <= step ? 'linear-gradient(90deg, ' + accent + ', ' + accent2 + ')' : '#2a2a3a',
                transition: 'all 0.3s',
              }} />
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#d6303120', border: '1px solid #d6303140', borderRadius: 12, padding: '12px 16px', marginBottom: 20, color: '#ff6b6b', fontSize: 14 }}>
            {error}
          </div>
        )}

        {/* Step 1: Business Info */}
        {step === 1 && (
          <div style={{ background: '#12121f', borderRadius: 20, padding: 32, border: '1px solid #1e1e35' }}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Business Name *</label>
              <input value={bizName} onChange={function(e) { setBizName(e.target.value); }}
                style={inputStyle} placeholder="Tony's Barbershop" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Phone Number</label>
              <input value={bizPhone} onChange={function(e) { setBizPhone(e.target.value); }}
                style={inputStyle} placeholder="(702) 555-0123" type="tel" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Business Email</label>
              <input value={bizEmail} onChange={function(e) { setBizEmail(e.target.value); }}
                style={inputStyle} placeholder="info@yourbusiness.com" type="email" />
            </div>
            <div style={{ marginBottom: 28 }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Address</label>
              <input value={bizAddress} onChange={function(e) { setBizAddress(e.target.value); }}
                style={inputStyle} placeholder="123 Main St, Las Vegas, NV" />
            </div>
            <button onClick={saveBusiness} disabled={loading} style={{
              width: '100%', padding: 16, background: 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')',
              border: 'none', borderRadius: 14, color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
            }}>{loading ? 'Saving...' : 'Continue \u2192'}</button>
          </div>
        )}

        {/* Step 2: Services */}
        {step === 2 && (
          <div style={{ background: '#12121f', borderRadius: 20, padding: 32, border: '1px solid #1e1e35' }}>
            {/* Added services */}
            {services.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: '#888', fontWeight: 600, marginBottom: 10 }}>Added:</div>
                {services.map(function(s, i) {
                  return (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '12px 16px',
                      background: '#00b89415', borderRadius: 10, marginBottom: 8, alignItems: 'center',
                    }}>
                      <div>
                        <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{s.name}</span>
                        <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>{s.duration} min</span>
                      </div>
                      <span style={{ color: '#00b894', fontWeight: 700, fontSize: 14 }}>${s.price}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add service form */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Service Name *</label>
              <input value={serviceName} onChange={function(e) { setServiceName(e.target.value); }}
                style={inputStyle} placeholder={tenant?.business_type === 'barbershop' ? 'Haircut' : tenant?.business_type === 'spa' ? 'Swedish Massage' : 'Service Name'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Duration</label>
                <select value={serviceDuration} onChange={function(e) { setServiceDuration(e.target.value); }} style={selectStyle}>
                  <option value="15">15 min</option>
                  <option value="30">30 min</option>
                  <option value="45">45 min</option>
                  <option value="60">60 min</option>
                  <option value="90">90 min</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Price *</label>
                <input value={servicePrice} onChange={function(e) { setServicePrice(e.target.value); }}
                  style={inputStyle} placeholder="50" type="number" min="0" step="5" />
              </div>
            </div>

            <button onClick={addService} disabled={loading} style={{
              width: '100%', padding: 14, background: '#1f2937', border: '1.5px solid #374151',
              borderRadius: 12, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 16,
            }}>{loading ? 'Adding...' : '+ Add Service'}</button>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={function() { setStep(1); }} style={{
                flex: 1, padding: 14, background: '#1f2937', border: 'none', borderRadius: 12,
                color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>{'\u2190'} Back</button>
              <button onClick={function() { if (services.length === 0) { setError('Add at least one service'); return; } setError(''); setStep(3); }}
                style={{
                  flex: 2, padding: 14, background: 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')',
                  border: 'none', borderRadius: 12, color: '#fff', fontSize: 15, fontWeight: 700,
                  cursor: 'pointer', opacity: services.length === 0 ? 0.5 : 1,
                }}>Continue {'\u2192'}</button>
            </div>
          </div>
        )}

        {/* Step 3: Hours */}
        {step === 3 && (
          <div style={{ background: '#12121f', borderRadius: 20, padding: 32, border: '1px solid #1e1e35' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {dayNames.map(function(day, i) {
                var h = hours[i];
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: h.open ? '#252840' : '#0f0f1a', borderRadius: 12, border: '1px solid #2a2a3a',
                  }}>
                    <div style={{ width: 90, fontSize: 14, fontWeight: 600, color: h.open ? '#fff' : '#666' }}>{day}</div>
                    <button onClick={function() {
                      setHours(function(prev) {
                        var updated = { ...prev };
                        updated[i] = { ...updated[i], open: !updated[i].open };
                        return updated;
                      });
                    }} style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: h.open ? accent : '#374151', position: 'relative', transition: 'all 0.2s',
                    }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: 9, background: '#fff',
                        position: 'absolute', top: 3, left: h.open ? 23 : 3, transition: 'all 0.2s',
                      }} />
                    </button>
                    {h.open ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        <input type="time" value={h.start} onChange={function(e) {
                          setHours(function(prev) {
                            var updated = { ...prev };
                            updated[i] = { ...updated[i], start: e.target.value };
                            return updated;
                          });
                        }} style={{ padding: '6px 10px', background: '#0f0f1a', border: '1px solid #2a2a4a', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                        <span style={{ color: '#666', fontSize: 13 }}>to</span>
                        <input type="time" value={h.end} onChange={function(e) {
                          setHours(function(prev) {
                            var updated = { ...prev };
                            updated[i] = { ...updated[i], end: e.target.value };
                            return updated;
                          });
                        }} style={{ padding: '6px 10px', background: '#0f0f1a', border: '1px solid #2a2a4a', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                      </div>
                    ) : (
                      <span style={{ color: '#666', fontSize: 13, fontStyle: 'italic' }}>Closed</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={function() { setStep(2); }} style={{
                flex: 1, padding: 14, background: '#1f2937', border: 'none', borderRadius: 12,
                color: '#ccc', fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}>{'\u2190'} Back</button>
              <button onClick={saveHours} disabled={loading} style={{
                flex: 2, padding: 14, background: 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')',
                border: 'none', borderRadius: 12, color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
              }}>{loading ? 'Saving...' : 'Continue \u2192'}</button>
            </div>
          </div>
        )}

        {/* Step 4: Done! */}
        {step === 4 && (
          <div style={{ background: '#12121f', borderRadius: 20, padding: 32, border: '1px solid #1e1e35', textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>{'\uD83C\uDF89'}</div>
            <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Your Booking Page is Live!</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 28 }}>Share this link with your customers so they can book online.</p>

            <div style={{
              background: '#252840', borderRadius: 14, padding: '16px 20px', marginBottom: 16,
              border: '1.5px solid #2a2a4a', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ color: '#888', fontSize: 13 }}>{window.location.origin}/book/</span>
                <span style={{ color: accent, fontSize: 13, fontWeight: 700 }}>{tenant?.subdomain}</span>
              </div>
              <button onClick={copyLink} style={{
                padding: '8px 16px', background: accent, border: 'none', borderRadius: 8,
                color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>Copy Link</button>
            </div>

            <a href={'/book/' + (tenant?.subdomain || '')} target="_blank" rel="noreferrer" style={{
              display: 'block', padding: '12px', background: '#1f2937', borderRadius: 10,
              color: '#ccc', fontSize: 14, fontWeight: 600, textDecoration: 'none', marginBottom: 24,
            }}>{'\uD83D\uDC41\uFE0F'} Preview Your Booking Page</a>

            <div style={{ background: '#00b89415', borderRadius: 14, padding: 20, marginBottom: 28, textAlign: 'left' }}>
              <div style={{ color: '#00b894', fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{'\u2705'} Setup Complete</div>
              <div style={{ color: '#aaa', fontSize: 13, lineHeight: 1.8 }}>
                {'\u2022'} Business name: <span style={{ color: '#fff', fontWeight: 600 }}>{bizName}</span><br />
                {'\u2022'} Services: <span style={{ color: '#fff', fontWeight: 600 }}>{services.length} added</span><br />
                {'\u2022'} Hours: <span style={{ color: '#fff', fontWeight: 600 }}>Configured</span><br />
                {'\u2022'} Booking link: <span style={{ color: '#fff', fontWeight: 600 }}>Live</span>
              </div>
            </div>

            <button onClick={finishSetup} style={{
              width: '100%', padding: 16, background: 'linear-gradient(135deg, ' + accent + ', ' + accent2 + ')',
              border: 'none', borderRadius: 14, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>Go to Dashboard {'\u2192'}</button>
          </div>
        )}

        {/* Skip link */}
        {step < 4 && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button onClick={finishSetup} style={{
              background: 'none', border: 'none', color: '#666', fontSize: 13, cursor: 'pointer',
            }}>Skip for now {'\u2192'}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SetupWizard;
